import { useEffect, useState } from 'react';
import {
  approveModerationPhoto,
  getModerationPhotos,
  mediaUrl,
  rejectModerationPhoto,
  type ModerationFilter,
  type ModerationFindings,
  type ModerationPhoto,
} from '../api';
import { badgeClass, fmtDate } from '../format';
import { useApi } from '../useApi';
import { Pager } from '../components/Pager';

const PAGE_SIZE = 60;

const FILTERS: ModerationFilter[] = ['all', 'pending', 'approved', 'rejected', 'flagged'];

/**
 * Compact "why flagged" chips from the AI findings. Null-safe: findings may be
 * absent (backend pipeline hasn't run, or AI disabled) — render nothing then.
 * These are advisory only; the human still decides via Approve / Reject.
 */
function Findings({ findings }: { findings?: ModerationFindings | null }) {
  if (!findings) return null;
  const chips: string[] = [];
  if (findings.duplicate) chips.push('Duplicate of another account');
  if (findings.explicit) chips.push('Possible explicit');
  if (findings.isPerson === false) chips.push('No face detected');
  if (findings.estimatedGender)
    chips.push(`Gender mismatch (estimated: ${findings.estimatedGender})`);
  if (findings.notes) chips.push(findings.notes);
  if (chips.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {chips.map((c, i) => (
        <span key={i} className="badge badge-warn" style={{ fontSize: '0.68rem' }}>
          {c}
        </span>
      ))}
    </div>
  );
}

/**
 * Thumbnail with a graceful fallback: the public stream serves APPROVED photos
 * only, so pending/rejected entries 404 there — show a placeholder tile.
 */
function Thumb({ photo }: { photo: ModerationPhoto }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          border: '1px dashed var(--border)',
          color: 'var(--text-2)',
          fontSize: '0.72rem',
          textAlign: 'center',
          padding: 8,
        }}
      >
        not streamable
        <br />({photo.moderationStatus})
      </div>
    );
  }
  return (
    <a href={mediaUrl(photo.url)} target="_blank" rel="noreferrer">
      <img
        src={mediaUrl(photo.url)}
        alt={`photo by ${photo.user.displayName ?? photo.user.username ?? photo.user.id}`}
        onError={() => setBroken(true)}
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          objectFit: 'cover',
          borderRadius: 8,
          display: 'block',
          border: '1px solid var(--border)',
        }}
      />
    </a>
  );
}

export function Moderation() {
  const [filter, setFilter] = useState<ModerationFilter>('all');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reset to the first page whenever the status filter changes.
  useEffect(() => { setPage(1); }, [filter]);

  const { data, error, loading, reload } = useApi(() => getModerationPhotos(filter, page, PAGE_SIZE), [filter, page]);
  const items = data?.items ?? [];

  const approve = async (p: ModerationPhoto) => {
    setBusyId(p.id);
    setActionError(null);
    try {
      await approveModerationPhoto(p.id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Approve failed.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (p: ModerationPhoto) => {
    const who = p.user.displayName ?? (p.user.username ? `@${p.user.username}` : p.user.id);
    if (
      !window.confirm(
        `Reject this photo by ${who}? It is deleted permanently and the user is notified by bot DM.`,
      )
    )
      return;
    setBusyId(p.id);
    setActionError(null);
    try {
      await rejectModerationPhoto(p.id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Reject failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Moderation<span className="dot">.</span>
        </h1>
        <div className="row-actions">
          {FILTERS.map((f) => (
            <button
              key={f}
              className="btn"
              style={
                f === filter
                  ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
                  : undefined
              }
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      {data && <p className="muted">{data.total} total{filter === 'all' ? '' : ` ${filter}`} (newest / flagged first)</p>}
      {actionError && <p className="error-line">{actionError}</p>}
      {error ? (
        <div className="table-wrap">
          <div className="empty">{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="table-wrap">
          <div className="empty">Loading…</div>
        </div>
      ) : items.length === 0 ? (
        <div className="table-wrap">
          <div className="empty">No {filter === 'all' ? '' : `${filter} `}photos in the queue.</div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 14,
            marginTop: 16,
          }}
        >
          {items.map((p) => (
            <div
              key={p.id}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Thumb photo={p} />
              <div style={{ minWidth: 0 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.user.displayName ?? <span className="muted">—</span>}
                  {p.user.username && <span className="muted"> @{p.user.username}</span>}
                </div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={badgeClass(p.moderationStatus)}>{p.moderationStatus}</span>
                  <span className="muted" style={{ fontSize: '0.72rem' }}>
                    {fmtDate(p.createdAt)}
                  </span>
                </div>
                <div style={{ marginTop: 6 }}>
                  <Findings findings={p.moderationFindings} />
                </div>
              </div>
              <div className="row-actions">
                <button
                  className="btn"
                  disabled={busyId === p.id || p.moderationStatus === 'approved'}
                  onClick={() => approve(p)}
                >
                  Approve
                </button>
                <button className="btn btn-danger" disabled={busyId === p.id} onClick={() => reject(p)}>
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {data && (
        <Pager page={data.page ?? page} pageSize={data.pageSize ?? PAGE_SIZE} total={data.total} loading={loading} onPage={setPage} />
      )}
    </div>
  );
}
